// Operational Engineering — formulas.
//
// Pure deterministic functions. Each returns NaN when its inputs are
// insufficient — never throws. Versioned so kill sheets record which
// formula module produced their outputs (calculation provenance).

import { PRESSURE_GRADIENT_PPG_PSI_PER_FT, asNum } from "../units";

// Local alias for the standard oilfield pressure gradient constant (0.052 psi/ft per ppg).
const G = PRESSURE_GRADIENT_PPG_PSI_PER_FT;

export const FORMULAS_VERSION = "openg-formulas/1.1";

// ---------------------------------------------------------------------------
// Hydrostatic & kill weight
// ---------------------------------------------------------------------------

/** Hydrostatic pressure of a column of mud (psi). */
export function hydrostaticPsi(mwPpg, tvdFt) {
  const a = asNum(mwPpg), b = asNum(tvdFt);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return NaN;
  return G * a * b;
}

/**
 * Kill Mud Weight (KMW), in ppg.
 *
 *   KMW = mwIn + SIDPP / (0.052 × TVD) + safetyMargin
 */
export function kmwPpg({ mwInPpg, sidppPsi, tvdFt, safetyMarginPpg = 0 }) {
  const mw  = asNum(mwInPpg);
  const sp  = asNum(sidppPsi);
  const tvd = asNum(tvdFt);
  const sm  = asNum(safetyMarginPpg, 0);
  if (!Number.isFinite(mw) || !Number.isFinite(sp) || !Number.isFinite(tvd) || tvd <= 0)
    return NaN;
  return mw + sp / (G * tvd) + sm;
}

// ---------------------------------------------------------------------------
// Standpipe pressures during kill
// ---------------------------------------------------------------------------

/** Initial Circulating Pressure (ICP, psi) = SIDPP + slow-pump pressure. */
export function icpPsi({ sidppPsi, slowPumpPressurePsi }) {
  const a = asNum(sidppPsi), b = asNum(slowPumpPressurePsi);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return NaN;
  return a + b;
}

/** Final Circulating Pressure (FCP, psi) = SCR × (KMW / MWin). */
export function fcpPsi({ slowPumpPressurePsi, kmwPpg, mwInPpg }) {
  const scr = asNum(slowPumpPressurePsi);
  const kmw = asNum(kmwPpg);
  const mw  = asNum(mwInPpg);
  if (!Number.isFinite(scr) || !Number.isFinite(kmw) || !Number.isFinite(mw) || mw <= 0)
    return NaN;
  return scr * (kmw / mw);
}


// ---------------------------------------------------------------------------
// MAASP — Maximum Allowable Annular Surface Pressure (Pass D).
//
// Two-step computation:
//   1. Max Allowable Mud Density (MAMW) — derived from the Leak-Off Test:
//        MAMW_ppg = (lotPressure_psi / (0.052 × shoeTVD_ft)) + mwAtLot_ppg
//
//   2. MAASP — the surface annulus pressure margin at any given mud weight:
//        MAASP_psi = (MAMW − currentMW) × 0.052 × shoeTVD_ft
//
// As mud weight goes UP, MAASP goes DOWN. When MAASP at the operative mud
// weight reaches zero, the casing shoe is exactly at fracture pressure —
// any additional surface pressure (e.g. trapped SICP) will break the shoe.
//
// Both functions are NaN-safe — they return NaN when inputs are missing
// or geometrically impossible, never throw.
// ---------------------------------------------------------------------------

/**
 * Max Allowable Mud Density (MAMW, ppg) from LOT data.
 *
 * `lotPressurePsi` is the surface pressure recorded at leak-off.
 * `mwAtLotPpg`     is the mud density in the hole during the LOT.
 * `shoeTvdFt`      is the casing shoe TVD where the LOT was performed.
 */
export function maxAllowableMudDensityPpg({ lotPressurePsi, mwAtLotPpg, shoeTvdFt }) {
  const lot  = asNum(lotPressurePsi);
  const mw   = asNum(mwAtLotPpg);
  const tvd  = asNum(shoeTvdFt);
  if (!Number.isFinite(lot) || !Number.isFinite(mw) || !Number.isFinite(tvd) || tvd <= 0)
    return NaN;
  return (lot / (G * tvd)) + mw;
}

/**
 * MAASP at a given current mud density (psi).
 *
 * `maxMudDensityPpg` is the MAMW returned by maxAllowableMudDensityPpg().
 * `currentMwPpg`     is the mud weight currently in the hole.
 * `shoeTvdFt`        is the casing shoe TVD.
 *
 * Returns NaN when inputs are missing. Returns ≤ 0 when the current mud
 * weight already exceeds the MAMW — a guardrail-breach signal the caller
 * surfaces in the UI / print.
 */
export function maaspPsi({ maxMudDensityPpg, currentMwPpg, shoeTvdFt }) {
  const mamw = asNum(maxMudDensityPpg);
  const cur  = asNum(currentMwPpg);
  const tvd  = asNum(shoeTvdFt);
  if (!Number.isFinite(mamw) || !Number.isFinite(cur) || !Number.isFinite(tvd) || tvd <= 0)
    return NaN;
  return (mamw - cur) * G * tvd;
}
